import React, { useCallback, useState } from 'react';
import lien from './lien';
import './css/dash.scss';

const Inscription = () => {
    const [email, setEmail] = useState("");
    const [inscriptionMessage, setInscriptionMessage] = useState("");
    const [isSuccess, setIsSuccess] = useState(false);
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [nom, setNom] = useState("");
    const [prenom, setPrenom] = useState("");
    const [prenomError, setPrenomError] = useState("");
    const [nomError, setNomError] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [confirmPasswordError, setConfirmPasswordError] = useState("");
    const [emailError, setEmailError] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    function validateEmail(mail) {
        if (/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(mail)) {
            setEmailError("");
            return true;
        }
        setEmailError("Adresse email invalide!");
        return false;
    }

    const validateForm = () => {
        let isValid = true;

        // Validation du nom
        if (nom === "") {
            setNomError("Le nom est obligatoire");
            isValid = false;
        } else {
            setNomError("");
        }

        // Validation du prénom
        if (prenom === "") {
            setPrenomError("Le prénom est obligatoire");
            isValid = false;
        } else {
            setPrenomError("");
        }

        // Validation de l'email
        if (!validateEmail(email)) {
            isValid = false;
        }

        // Validation du mot de passe
        if (password.length < 3) {
            setPasswordError("Le mot de passe doit comporter au moins 3 caractères");
            isValid = false;
        } else {
            setPasswordError("");
        }

        // Validation de la confirmation du mot de passe
        if (password !== confirmPassword) {
            setConfirmPasswordError("Les mots de passe ne correspondent pas");
            isValid = false;
        } else {
            setConfirmPasswordError("");
        }

        return isValid;
    };

    const fetchInscription = useCallback(async (e) => {
        e.preventDefault();

        // Réinitialisation des messages
        setInscriptionMessage("");
        setIsSuccess(false);

        // Validation du formulaire
        if (!validateForm()) {
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch(
                lien.url + "connection/signup",
                {
                    method: "POST",
                    body: JSON.stringify({
                        "nom": nom,
                        "prenom": prenom,
                        "password": password,
                        "email": email
                    }),
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.ok) {
                setIsSuccess(true);
                setInscriptionMessage("Inscription réussie! Un email de vérification a été envoyé à votre adresse email. Veuillez vérifier votre boîte de réception et suivre les instructions pour activer votre compte.");

                // Réinitialisation du formulaire
                setNom("");
                setPrenom("");
                setEmail("");
                setPassword("");
                setConfirmPassword("");
            } else {
                const data = await response.json().catch(() => ({}));
                setIsSuccess(false);
                setInscriptionMessage(data.message || "Une erreur s'est produite lors de l'inscription. Veuillez réessayer.");
            }
        } catch (error) {
            setIsSuccess(false);
            setInscriptionMessage("Erreur de connexion au serveur. Veuillez réessayer plus tard.");
            console.error("Erreur d'inscription:", error);
        } finally {
            setIsLoading(false);
        }
    }, [nom, prenom, email, password, confirmPassword]);

    return (
        <div className="inscription-container">
            <div className="form-card">
                <h2>Créer un compte</h2>

                {inscriptionMessage && (
                    <div className={isSuccess ? "success-message" : "error-message"}>
                        {inscriptionMessage}
                    </div>
                )}

                <form onSubmit={fetchInscription}>
                    <div className="form-group">
                        <label htmlFor="nom">Nom</label>
                        <input
                            type="text"
                            id="nom"
                            value={nom}
                            placeholder="Votre nom"
                            onChange={e => {
                                setNom(e.target.value);
                                if (e.target.value === "") {
                                    setNomError("Le nom est obligatoire");
                                } else {
                                    setNomError("");
                                }
                            }}
                        />
                        {nomError && <p className="error">{nomError}</p>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="prenom">Prénom</label>
                        <input
                            type="text"
                            id="prenom"
                            value={prenom}
                            placeholder="Votre prénom"
                            onChange={e => {
                                setPrenom(e.target.value);
                                if (e.target.value === "") {
                                    setPrenomError("Le prénom est obligatoire");
                                } else {
                                    setPrenomError("");
                                }
                            }}
                        />
                        {prenomError && <p className="error">{prenomError}</p>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">Email</label>
                        <input
                            type="email"
                            id="email"
                            value={email}
                            placeholder="Votre adresse email"
                            onChange={e => {
                                setEmail(e.target.value);
                                if (e.target.value) {
                                    validateEmail(e.target.value);
                                }
                            }}
                        />
                        {emailError && <p className="error">{emailError}</p>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">Mot de passe</label>
                        <input
                            type="password"
                            id="password"
                            value={password}
                            placeholder="Votre mot de passe"
                            onChange={e => {
                                setPassword(e.target.value);
                                if (e.target.value.length < 3) {
                                    setPasswordError("Le mot de passe doit comporter au moins 3 caractères");
                                } else {
                                    setPasswordError("");
                                }

                                // Vérifier la correspondance si la confirmation est déjà remplie
                                if (confirmPassword) {
                                    if (e.target.value !== confirmPassword) {
                                        setConfirmPasswordError("Les mots de passe ne correspondent pas");
                                    } else {
                                        setConfirmPasswordError("");
                                    }
                                }
                            }}
                        />
                        {passwordError && <p className="error">{passwordError}</p>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="confirmPassword">Confirmer le mot de passe</label>
                        <input
                            type="password"
                            id="confirmPassword"
                            value={confirmPassword}
                            placeholder="Confirmez votre mot de passe"
                            onChange={e => {
                                setConfirmPassword(e.target.value);
                                if (e.target.value !== password) {
                                    setConfirmPasswordError("Les mots de passe ne correspondent pas");
                                } else {
                                    setConfirmPasswordError("");
                                }
                            }}
                        />
                        {confirmPasswordError && <p className="error">{confirmPasswordError}</p>}
                    </div>

                    <div className="info-message">
                        <p>Un email de validation vous sera envoyé après l'inscription pour activer votre compte.</p>
                    </div>

                    <button
                        type="submit"
                        className={isLoading ? "loading-button" : "submit-button"}
                        disabled={isLoading}
                    >
                        {isLoading ? "Inscription en cours..." : "S'inscrire"}
                    </button>
                </form>

                <div className="form-footer">
                    <p>
                        Vous avez déjà un compte? <a href="/login">Se connecter</a>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Inscription;